/**
 * Widget Safe House para o WordPress.
 *
 * Uso (bloco HTML do Elementor ou do editor):
 *   <div id="safehouse"></div>
 *   <script src="https://orcamento.prosegsmart.com.br/widget.js"
 *           data-alvo="#safehouse"
 *           data-turnstile="SITE_KEY"
 *           data-moldura="nao"
 *           (data-privacidade="URL" opcional; sem ele, usa https://orcamento.prosegsmart.com.br/privacidade)
 *           data-topo="80" async></script>
 *
 * Tudo roda num Shadow DOM: o CSS do tema e do Elementor não entra, e o nosso não vaza para o site.
 */
import { calcularOrcamento, CATALOGO_V1, type Catalogo, type ChaveProduto, type EscolhasEntrada } from '../src/core';
import { DCLogic, montar, type ComponenteDC } from './runtime';
// @ts-ignore módulo gerado pelo build (JS)
import { CSS, MOLDE, criarClasse } from './gerado/tela.js';

declare const __VERSAO__: string;

const EVENTOS_SERVIDOR = new Set([
  'pagina_vista',
  'quiz_inicio',
  'passo_visto',
  'passo_ok',
  'voltar',
  'preco_visto',
  'form_inicio',
  'whatsapp_clique',
  'enviado_familia',
  'pdf_baixar',
  'faq_aberta',
  'ativo',
]);

interface Opcoes {
  api: string;
  alvo: Element;
  turnstile: string;
  moldura: boolean;
  privacidade: string;
  /** Altura do cabeçalho fixo do site. -1 = medir sozinho (data-topo="auto" ou ausente). */
  topo: number;
}

const w = window as any;

function guardar(chave: string, valor?: string): string | null {
  try {
    if (valor !== undefined) sessionStorage.setItem(chave, valor);
    return sessionStorage.getItem(chave);
  } catch {
    return valor ?? null;
  }
}

function aleatorio(n: number): string {
  const abc = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const b = new Uint8Array(n);
  try {
    crypto.getRandomValues(b);
  } catch {
    for (let i = 0; i < n; i++) b[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(b, (x) => abc[x % abc.length]).join('');
}

/** Sessão do visitante (por aba). Liga os eventos do funil ao orçamento. */
function sessao(): string {
  const s = guardar('safehouse_sessao');
  if (s && s.length >= 8) return s;
  return guardar('safehouse_sessao', aleatorio(24)) || aleatorio(24);
}

/** Origem do visitante: guarda a primeira (first touch) da sessão. */
function origem(): Record<string, string> {
  const salva = guardar('safehouse_origem');
  if (salva) {
    try {
      return JSON.parse(salva);
    } catch {
      /* segue */
    }
  }
  const o: Record<string, string> = {};
  try {
    const q = new URLSearchParams(location.search);
    for (const k of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid', 'gclid']) {
      const v = q.get(k);
      if (v) o[k] = v.slice(0, k === 'fbclid' || k === 'gclid' ? 300 : 200);
    }
    if (document.referrer) o.referrer = document.referrer.slice(0, 300);
    o.pagina = (location.origin + location.pathname).slice(0, 300);
  } catch {
    /* segue */
  }
  guardar('safehouse_origem', JSON.stringify(o));
  return o;
}

/** De onde veio a visita, em poucas palavras (a Central mostra "Google Ads", "Meta Ads", "Direto"...). */
function classificarFonte(o: Record<string, string>): string {
  const src = (o.utm_source || '').toLowerCase();
  if (o.gclid || /google|adwords|gads/.test(src)) return 'google_ads';
  if (o.fbclid || /facebook|instagram|meta|^fb$|^ig$/.test(src)) return 'meta';
  if (src) return 'campanha';
  let host = '';
  try {
    host = o.referrer ? new URL(o.referrer).hostname.toLowerCase() : '';
  } catch {
    /* referrer inválido */
  }
  if (!host) return 'direto';
  if (host === location.hostname.toLowerCase() || host.endsWith('.' + location.hostname.toLowerCase())) return 'site';
  if (/(^|\.)(google|bing|duckduckgo|yahoo|ecosia)\./.test(host)) return 'busca';
  if (/facebook|instagram|^t\.co$|linktr|whatsapp|youtube|tiktok|l\.wl\.co/.test(host)) return 'social';
  return 'outro';
}

/** Robôs (Google, auditorias de velocidade, pré-visualizações) não entram na contagem do funil. */
function pareceRobo(): boolean {
  try {
    return !!navigator.webdriver || /bot|crawl|spider|headless|lighthouse|pagespeed|preview|facebookexternalhit/i.test(navigator.userAgent);
  } catch {
    return false;
  }
}

/**
 * Altura ocupada no topo da tela por cabeçalhos fixos/grudados do site (menu do Elementor, barra do WordPress…).
 * Com data-topo numérico usa o número; sem ele, mede: desce pelo meio da tela até achar algo que não seja fixo.
 */
function alturaTopo(fixo: number, host: Element): number {
  if (fixo >= 0) return fixo;
  try {
    const x = Math.round((window.innerWidth || 800) / 2);
    for (let y = 0; y < 320; y += 6) {
      const el = document.elementFromPoint(x, y);
      if (!el || el === host || host.contains(el)) return y;
      let fixoAqui = false;
      for (let n: Element | null = el; n && n !== document.body; n = n.parentElement) {
        const p = getComputedStyle(n).position;
        if (p === 'fixed' || p === 'sticky') {
          fixoAqui = true;
          break;
        }
      }
      if (!fixoAqui) return y;
    }
    return 320;
  } catch {
    return 0;
  }
}

/** Rola sem animação, mesmo se o site usar scroll-behavior: smooth (animação concorrente deixava a tela parada). */
function rolarInstantaneo(top: number) {
  try {
    window.scrollTo({ top: Math.max(0, top), behavior: 'instant' as ScrollBehavior });
  } catch {
    window.scrollTo(0, Math.max(0, top));
  }
}

/** Pixel da Meta, GA4 (gtag) e GTM (dataLayer), se o site tiver. Nunca quebra o widget. */
function rastrear(tipo: string, dados: Record<string, unknown> = {}, idEvento?: string) {
  try {
    (w.dataLayer = w.dataLayer || []).push(Object.assign({ event: 'safehouse_' + tipo }, dados));
  } catch {
    /* sem GTM */
  }
  const fb = (...a: unknown[]) => {
    try {
      if (typeof w.fbq === 'function') w.fbq(...a);
    } catch {
      /* sem pixel */
    }
  };
  const gt = (...a: unknown[]) => {
    try {
      if (typeof w.gtag === 'function') w.gtag(...a);
    } catch {
      /* sem GA4 */
    }
  };
  const valor = typeof dados.total === 'number' ? { value: (dados.total as number) / 100, currency: 'BRL' } : {};
  const extra = idEvento ? { eventID: idEvento } : undefined;
  switch (tipo) {
    case 'quiz_inicio':
      fb('trackCustom', 'SafeHouseInicio');
      gt('event', 'safehouse_inicio');
      break;
    case 'preco_visto':
      fb('track', 'ViewContent', Object.assign({ content_name: 'Alarme Safe House' }, valor));
      gt('event', 'view_item', valor);
      break;
    case 'lead':
      fb('track', 'Lead', valor, extra);
      gt('event', 'generate_lead', valor);
      break;
    case 'whatsapp_clique':
      fb('track', 'Contact');
      gt('event', 'contact', { method: 'whatsapp' });
      break;
    case 'agendar':
      fb('track', 'Schedule', {}, extra);
      gt('event', 'safehouse_agendar');
      break;
    case 'pdf_baixar':
      gt('event', 'file_download', { file_name: String(dados.arquivo || '') });
      break;
  }
}

// guardado já na carga: depois do DOMContentLoaded o currentScript some
const scriptAtual = document.currentScript as HTMLScriptElement | null;

function lerOpcoes(): Opcoes | null {
  const script =
    scriptAtual ||
    (document.querySelector('script[src*="widget.js"][data-alvo], script[src*="/widget.js"]') as HTMLScriptElement | null);
  const d = script?.dataset ?? {};
  let api = d.api || '';
  if (!api && script?.src) api = new URL(script.src, location.href).origin;
  if (!api) api = location.origin;
  let alvo: Element | null = d.alvo ? document.querySelector(d.alvo) : document.getElementById('safehouse');
  if (!alvo && script?.parentNode) {
    alvo = document.createElement('div');
    script.parentNode.insertBefore(alvo, script.nextSibling);
  }
  if (!alvo) return null;
  return {
    api: api.replace(/\/+$/, ''),
    alvo,
    turnstile: d.turnstile || '',
    moldura: !/^(nao|não|false|0)$/i.test(d.moldura || ''),
    privacidade: d.privacidade || api.replace(/\/+$/, '') + '/privacidade',
    topo: !d.topo || /^auto$/i.test(d.topo) ? -1 : Number(d.topo) || 0,
  };
}

/** Manrope servida pelo nosso servidor. @font-face precisa ficar no documento (dentro do Shadow DOM o Chrome ignora). */
function injetarFontes(api: string) {
  if (document.getElementById('safehouse-fontes')) return;
  const st = document.createElement('style');
  st.id = 'safehouse-fontes';
  st.textContent = [400, 500, 600, 700, 800]
    .map(
      (p) =>
        `@font-face{font-family:'Manrope';font-style:normal;font-weight:${p};font-display:swap;src:url('${api}/widget/fontes/manrope-latin-${p}-normal.woff2') format('woff2')}`,
    )
    .join('');
  document.head.appendChild(st);
}

async function chamar<T>(api: string, metodo: 'GET' | 'POST', caminho: string, corpo?: unknown, ms = 30000): Promise<T> {
  const ctl = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const t = ctl ? setTimeout(() => ctl.abort(), ms) : null;
  let r: Response;
  try {
    r = await fetch(api + caminho, {
      method: metodo,
      headers: corpo !== undefined ? { 'Content-Type': 'application/json' } : undefined,
      body: corpo !== undefined ? JSON.stringify(corpo) : undefined,
      credentials: 'omit',
      signal: ctl?.signal,
    });
  } catch {
    throw new Error('Não conseguimos falar com o servidor. Confira a internet e tente de novo.');
  } finally {
    if (t) clearTimeout(t);
  }
  if (r.status === 204) return undefined as T;
  let j: any = null;
  try {
    j = await r.json();
  } catch {
    /* sem corpo */
  }
  if (!r.ok) throw new Error((j && j.mensagem) || 'Algo deu errado. Tente de novo ou fale com a equipe no WhatsApp.');
  return j as T;
}

/** Cloudflare Turnstile no modo invisível. O quadrinho fica no light DOM (slot "turnstile") e só aparece se o Cloudflare pedir. */
function criarTurnstile(siteKey: string, recipiente: HTMLElement) {
  let carregando: Promise<any> | null = null;
  let id: string | undefined;
  let pendente: { ok: (t: string) => void; erro: () => void } | null = null;
  const carregar = () => {
    if (!siteKey) return Promise.resolve(null);
    if (w.turnstile) return Promise.resolve(w.turnstile);
    if (!carregando) {
      carregando = new Promise((ok, erro) => {
        const s = document.createElement('script');
        s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        s.async = true;
        s.onload = () => ok(w.turnstile);
        s.onerror = () => {
          carregando = null;
          erro(new Error('turnstile'));
        };
        document.head.appendChild(s);
      });
    }
    return carregando;
  };
  return {
    preparar() {
      carregar().catch(() => undefined);
    },
    /** Token novo para cada envio. Se o Turnstile não carregar (bloqueador), segue sem; o servidor decide. */
    async token(): Promise<string | undefined> {
      if (!siteKey) return undefined;
      let ts: any;
      try {
        ts = await carregar();
      } catch {
        return undefined;
      }
      if (!ts) return undefined;
      return new Promise<string | undefined>((ok) => {
        const fim = setTimeout(() => {
          pendente = null;
          ok(undefined);
        }, 45000);
        pendente = {
          ok: (t) => {
            clearTimeout(fim);
            ok(t);
          },
          erro: () => {
            clearTimeout(fim);
            ok(undefined);
          },
        };
        try {
          if (id === undefined) {
            id = ts.render(recipiente, {
              sitekey: siteKey,
              execution: 'execute',
              appearance: 'interaction-only',
              language: 'pt-br',
              callback: (t: string) => pendente?.ok(t),
              'error-callback': () => pendente?.erro(),
              'expired-callback': () => undefined,
            });
          } else ts.reset(id);
          ts.execute(id);
        } catch {
          pendente?.erro();
        }
      });
    },
  };
}

function iniciar() {
  const op = lerOpcoes();
  if (!op) return;
  if ((op.alvo as any).__safehouse) return; // já montado (script colado duas vezes)
  (op.alvo as any).__safehouse = true;
  injetarFontes(op.api);

  const host = op.alvo as HTMLElement;
  const sombra = host.attachShadow({ mode: 'open' });
  const estilo = document.createElement('style');
  // ao rolar para um bloco, não esconder o título atrás do cabeçalho fixo do site
  estilo.textContent = CSS;
  sombra.appendChild(estilo);

  const caixaTs = document.createElement('div');
  caixaTs.setAttribute('slot', 'turnstile');
  caixaTs.style.cssText = 'display:flex;justify-content:center';
  host.appendChild(caixaTs);
  const ts = criarTurnstile(op.turnstile, caixaTs);

  let catalogo: Catalogo = CATALOGO_V1;
  const idSessao = sessao();
  const dadosOrigem = origem();
  let codigoAtual = '';

  const ctx = {
    versao: __VERSAO__,
    moldura: op.moldura,
    logoEmpresa: op.api + "/marca/logo-proseg.png",
    urlPrivacidade: op.privacidade,
    preco(chave: ChaveProduto): number {
      return (catalogo.produtos[chave]?.precoCentavos ?? 0) / 100;
    },
    calcular(escolhas: EscolhasEntrada) {
      try {
        return calcularOrcamento(catalogo, escolhas);
      } catch {
        return null;
      }
    },
    evento(tipo: string, dados: Record<string, unknown> = {}) {
      if (tipo === 'preco_visto' || tipo === 'form_inicio') ts.preparar();
      if (tipo !== 'ativo') rastrear(tipo, dados);
      if (!EVENTOS_SERVIDOR.has(tipo) || pareceRobo()) return;
      const corpo: Record<string, unknown> = { tipo, sessao: idSessao, dados };
      if (codigoAtual) corpo.codigo = codigoAtual;
      try {
        void fetch(op.api + '/api/eventos', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(corpo),
          credentials: 'omit',
          keepalive: true,
        }).catch(() => undefined);
      } catch {
        /* evento é opcional */
      }
    },
    async criarOrcamento(escolhas: EscolhasEntrada, cliente: { nome: string; whatsapp: string }) {
      const turnstile = await ts.token();
      const r = await chamar<{
        numero: string;
        codigo: string;
        totalCentavos: number;
        validadeAte: string;
        pdfPronto: boolean;
        links: { orcamento: string; projeto: string; agendar: string; whatsapp: string };
      }>(op.api, 'POST', '/api/orcamentos', { escolhas, cliente, origem: dadosOrigem, sessao: idSessao, turnstile }, 40000);
      codigoAtual = r.codigo;
      rastrear('lead', { total: r.totalCentavos, numero: r.numero }, 'lead-' + r.codigo);
      return r;
    },
    situacao(codigo: string) {
      return chamar<{ pdfPronto: boolean; paginasProjeto: number | null }>(
        op.api,
        'GET',
        '/api/orcamentos/' + encodeURIComponent(codigo) + '/situacao',
      );
    },
    enviarEmail(codigo: string, email: string) {
      return chamar(op.api, 'POST', '/api/orcamentos/' + encodeURIComponent(codigo) + '/email', { email });
    },
    async agendar(codigo: string) {
      const r = await chamar(op.api, 'POST', '/api/orcamentos/' + encodeURIComponent(codigo) + '/agendar', {});
      rastrear('agendar', {}, 'agendar-' + codigo);
      return r;
    },
    /** Abre o PDF. Chamado direto no clique (senão o navegador bloqueia a nova aba). */
    abrir(url: string) {
      let janela: Window | null = null;
      try {
        janela = window.open(url, '_blank');
      } catch {
        janela = null;
      }
      if (janela) {
        try {
          janela.opener = null;
        } catch {
          /* ok */
        }
      } else location.href = url; // navegador do Instagram/Facebook sem nova aba: abre na mesma
    },
    /** Volta ao começo do widget (não ao topo do site). */
    rolarTopo() {
      const topo = alturaTopo(op.topo, host);
      const r = host.getBoundingClientRect();
      if (r.top < topo) rolarInstantaneo(window.pageYOffset + r.top - topo);
    },
    /** Rola até um bloco do widget, sem animação, deixando o título abaixo do cabeçalho fixo do site. */
    rolarPara(alvo: Element) {
      const topo = alturaTopo(op.topo, host);
      const r = alvo.getBoundingClientRect();
      const h = window.innerHeight || 800;
      if (r.top < topo || r.top > h * 0.5) rolarInstantaneo(window.pageYOffset + r.top - topo - 12);
    },
  };

  const docSombra = { getElementById: (id: string) => sombra.getElementById(id) };
  const Component = criarClasse(DCLogic, docSombra, op.api + '/widget/img') as new (p: Record<string, any>) => ComponenteDC;
  const m = montar(Component, sombra, MOLDE, { ctx });
  w.SafeHouse = { versao: __VERSAO__, ctx, montagem: m };
  {
    const o = origem();
    const dadosVisita: Record<string, string> = { fonte: classificarFonte(o), tela: (window.innerWidth || 800) < 760 ? 'celular' : 'pc' };
    if (o.utm_campaign) dadosVisita.campanha = o.utm_campaign.slice(0, 120);
    ctx.evento('pagina_vista', dadosVisita);
  }
  // batida a cada 45 s enquanto a aba está visível (até ~25 min): só para a Central saber quem está online agora
  {
    let batidas = 0;
    const timerBatida = window.setInterval(() => {
      if (++batidas > 33) return window.clearInterval(timerBatida);
      if (document.visibilityState === 'visible') ctx.evento('ativo', {});
    }, 45_000);
  }

  // catálogo publicado na Central (preços atuais); o v1 embutido cobre a falta de rede
  chamar<Catalogo>(op.api, 'GET', '/api/catalogo', undefined, 15000)
    .then((c) => {
      if (c && c.produtos && c.versao) {
        catalogo = c;
        m.desenharAgora();
      }
    })
    .catch(() => undefined);
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', iniciar);
else iniciar();
